"use client";

import { useEffect, useMemo, useState } from "react";

export type ReviewCard = {
  rating: number;
  title: string;
  date: string;
  packageQuantity: string;
  quote: string;
  name: string;
  helpful?: string;
};

type ScrollingReviewMarqueeProps = {
  reviews: ReviewCard[];
};

export default function ScrollingReviewMarquee({
  reviews,
}: Readonly<ScrollingReviewMarqueeProps>) {
  const [isPaused, setIsPaused] = useState(false);

  useEffect(() => {
    if (!isPaused) return;

    const resume = () => setIsPaused(false);
    globalThis.addEventListener("pointerup", resume);
    globalThis.addEventListener("pointercancel", resume);

    return () => {
      globalThis.removeEventListener("pointerup", resume);
      globalThis.removeEventListener("pointercancel", resume);
    };
  }, [isPaused]);

  const marqueeReviews = useMemo(
    () => [
      ...reviews.map((review, index) => ({
        ...review,
        pass: "a",
        key: `a-${review.name}-${review.date}-${index}`,
      })),
      ...reviews.map((review, index) => ({
        ...review,
        pass: "b",
        key: `b-${review.name}-${review.date}-${index}`,
      })),
    ],
    [reviews],
  );

  return (
    <div
      className="reviews-row"
      onPointerDown={() => setIsPaused(true)}
      onPointerUp={() => setIsPaused(false)}
      onPointerCancel={() => setIsPaused(false)}
    >
      <div
        className="reviews-track"
        style={{ animationPlayState: isPaused ? "paused" : "running" }}
      >
        {marqueeReviews.map((review) => (
          <article
            key={review.key}
            className="min-w-[280px] max-w-[360px] rounded-2xl border border-white/10 bg-black/60 p-4 backdrop-blur-glass shadow-glass transition duration-300 ease-out will-change-transform hover:-translate-y-0.5 hover:brightness-110 hover:shadow-glass"
          >
            <div className="text-sm font-semibold text-white/95">{review.name}</div>
            <div
              className="mt-1 flex items-center gap-2 text-[#F39C37]"
              style={{ textShadow: "none" }}
            >
              <span>
                {Array.from({ length: 5 }).map((_, index) => (
                  <span key={`${review.key}-star-${index}`}>
                    {index < review.rating ? "\u2605" : "\u2606"}
                  </span>
                ))}
              </span>
              <span className="font-bold text-white">{review.title}</span>
            </div>
            <p className="mt-1 text-sm text-white/85">
              Reviewed in the United States on {review.date}
            </p>
            <p className="mt-1 text-xs text-white/80">
              Item Package Quantity: {review.packageQuantity}
            </p>
            <p className="mt-2 text-sm text-white/90">&quot;{review.quote}&quot;</p>
            {review.helpful ? (
              <p className="mt-3 text-xs text-white/70">{review.helpful}</p>
            ) : null}
            <p className="mt-2 text-xs font-semibold text-white/70">Customer review</p>
          </article>
        ))}
      </div>
    </div>
  );
}
